import { memo, useRef, useState } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Menu as BaseMenu } from '@base-ui/react/menu';
import { CheckCircleFilledIcon, CheckCircleIcon, CopyIcon, MessageSquareIcon, PencilIcon, TrashIcon } from '@utils/icons';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import { readMark, readProgress } from '@d-orca/shared/client/utils';
import { OPEN_ACTIVITY_PARAM } from '@constants/activitiesParams';
import type { CommsMessage, EntityRef, MessageSegment } from '@interfaces/ICommsApi';
import type { IPendingAttachment } from '../../../hooks/useComposerAttachments';
import { AttachmentView, LocalAttachmentView } from '../AttachmentView/AttachmentView';
import { Avatar } from '@components/ui/Avatar/Avatar';
import styles from './MessageItem.module.css';
import { isImeComposing } from '@utils/ime';

interface IMessageItemProps {
  message: CommsMessage;
  isOwn: boolean;
  grouped?: boolean;
  onRetry: (message: CommsMessage) => void;
  /** Drops a message that failed to send. */
  onDiscard: (message: CommsMessage) => void;
  /** The files of a message still on this device (sending, or failed to send). */
  localAttachments?: IPendingAttachment[];
  onDelete?: () => void;
  onEdit?: (segments: MessageSegment[]) => Promise<void>;
  onSendDirectMessage?: () => void;
  onInfo?: () => void;
}

/**
 * Flatten segments to plain text for the inline editor.
 * Mention segments lose their structured metadata (kind/id) and become
 * plain "@label" text — editing a message with mentions deliberately
 * downgrades them. This is an accepted v1 limitation; the edit entry point
 * is only shown when hasText is true so voice-only messages are excluded.
 */
function segmentsToPlainText(segments: MessageSegment[]): string {
  return segments
    .map((s) => (s.kind === 'text' ? s.text : `@${s.ref.label}`))
    .join('');
}

// The viewer's zone, like every time in comms — chat is between people who may
// be in different places, and a time should match the reader's own clock.
const TIME_FMT = new Intl.DateTimeFormat('ja-JP', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

function MentionChip({ entity, siteId }: { entity: EntityRef; siteId: string | undefined }) {
  const navigate = useNavigate();
  function handleClick() {
    if (!siteId) return;
    switch (entity.kind) {
      case 'activity':
        // The Activities page opens `?openActivity=<id>` in its task drawer.
        navigate(`/activities/${siteId}?${OPEN_ACTIVITY_PARAM}=${encodeURIComponent(entity.id)}`);
        break;
      case 'member':
        // not navigable yet
        break;
    }
  }
  return (
    <button
      type="button"
      className={`${styles.mentionChip} ${styles[`mention_${entity.kind}`] ?? ''}`} // a kind no longer offered (a stored document mention) keeps the base look
      onClick={handleClick}
    >
      @{entity.label}
    </button>
  );
}

function SegmentView({ segment, siteId }: { segment: MessageSegment; siteId: string | undefined }) {
  if (segment.kind === 'text') return <span>{segment.text}</span>;
  return <MentionChip entity={segment.ref} siteId={siteId} />;
}

const MARK_TIP = { sent: 'sentTip', partial: 'partialTip', read: 'readTip' } as const;

/**
 * The mark on one of your own messages — a check on a disc: red while no one
 * it was for has read it, grey once some have (with "read by 3 of 8" beside
 * it), green once everyone has. The tooltip says which. A message with no one
 * else to read it carries no mark.
 */
function ReadMark({ message }: { message: CommsMessage }) {
  const { t } = useTranslation();
  const mark = readMark(message);
  const progress = readProgress(message);
  if (mark === 'pending' || mark === 'failed' || !progress) return null;
  const tip = t(`comms.receipts.${MARK_TIP[mark]}`, progress);
  return (
    <Tooltip content={tip}>
      <span className={styles.mark} data-mark={mark} aria-label={tip}>
        <CheckCircleFilledIcon size={13} />
        {mark === 'partial' && (
          <span className={styles.readProgress}>{t('comms.receipts.progress', progress)}</span>
        )}
      </span>
    </Tooltip>
  );
}

function MessageItemInner({ message, isOwn, grouped = false, onRetry, onDiscard, localAttachments, onDelete, onEdit, onSendDirectMessage, onInfo }: IMessageItemProps) {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const time = TIME_FMT.format(new Date(message.createdAt));
  const isDeleted = !!message.deletedAt;
  const isEdited = !!message.editedAt;
  const hasText = message.segments.some((segment) => (segment.kind === 'text' ? segment.text.length > 0 : true));
  const canDelete = isOwn && !isDeleted && !message.status;
  const canEdit = isOwn && !isDeleted && !message.status && hasText;
  const canCopy = !isDeleted && hasText;
  const canSendDirectMessage = !!onSendDirectMessage;
  const canInfo = !!onInfo && isOwn && !isDeleted && !message.status;
  const canOpenMenu = canDelete || canEdit || canCopy || canSendDirectMessage || canInfo;

  // Context menu: programmatic open via a hidden anchor positioned at the
  // right-click coordinates.
  const [menuOpen, setMenuOpen] = useState(false);
  const [anchorPos, setAnchorPos] = useState<{ x: number; y: number } | null>(null);
  const anchorRef = useRef<HTMLButtonElement>(null);

  // Inline edit state. When set, the text row swaps to a textarea.
  const [editDraft, setEditDraft] = useState<string | null>(null);
  const [editError, setEditError] = useState(false);
  // Capture the original text once so commitEdit can compare without re-running segmentsToPlainText.
  const originalTextRef = useRef<string>('');

  function handleContextMenu(e: React.MouseEvent) {
    e.preventDefault();
    if (!canOpenMenu) return;
    setAnchorPos({ x: e.clientX, y: e.clientY });
    setMenuOpen(true);
  }

  function copyText() {
    navigator.clipboard?.writeText(segmentsToPlainText(message.segments)).catch(() => {});
    setMenuOpen(false);
  }

  function sendDirectMessage() {
    onSendDirectMessage?.();
    setMenuOpen(false);
  }

  function startEdit() {
    const original = segmentsToPlainText(message.segments);
    originalTextRef.current = original;
    setEditDraft(original);
    setEditError(false);
    setMenuOpen(false);
  }

  async function commitEdit() {
    if (!onEdit || editDraft === null) return;
    const trimmed = editDraft.trim();
    if (trimmed.length === 0) return;
    if (trimmed === originalTextRef.current.trim()) {
      setEditDraft(null);
      return;
    }
    setEditError(false);
    try {
      await onEdit([{ kind: 'text', text: trimmed }]);
      setEditDraft(null);
    } catch {
      setEditError(true);
    }
  }

  function cancelEdit() {
    setEditDraft(null);
    setEditError(false);
  }

  // Sending / failed-to-send, with Retry and Discard. In the meta row for the
  // first message of a group; under the body for a grouped one, which has no meta row.
  const deliveryStatus = message.status === 'pending' ? (
    <span className={styles.statusPending}>{t('comms.messages.sending')}</span>
  ) : message.status === 'failed' ? (
    <span className={styles.statusFailed}>
      {t('comms.messages.failed')}
      {' · '}
      <button type="button" className={styles.retryBtn} onClick={() => onRetry(message)}>
        {t('comms.messages.retry')}
      </button>
      {' · '}
      <button type="button" className={styles.retryBtn} onClick={() => onDiscard(message)}>
        {t('comms.messages.discard')}
      </button>
    </span>
  ) : null;

  const rowClass = [
    styles.row,
    grouped ? styles.rowGrouped : '',
    message.status === 'pending' ? styles.rowPending : '',
    message.status === 'failed' ? styles.rowFailed : '',
    isDeleted ? styles.rowDeleted : '',
    menuOpen ? styles.rowMenuOpen : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={rowClass} onContextMenu={handleContextMenu}>
      {grouped ? (
        <div className={styles.avatarSpacer} aria-hidden="true" />
      ) : (
        <Avatar name={message.authorName} size={25} tinted className={styles.avatar} />
      )}

      <div className={styles.body}>
        {/* Full meta row — author name + time — for the first message in a group */}
        {!grouped && (
          <div className={styles.meta}>
            <span className={styles.metaLeft}>
              <span className={styles.author}>{message.authorName}</span>
              {isOwn && <span className={styles.youTag}>{t('comms.messages.youTag')}</span>}
              <span className={styles.time}>{time}</span>
              {deliveryStatus}
            </span>
            {isOwn && (
              <span className={styles.metaRight}>
                <ReadMark message={message} />
              </span>
            )}
          </div>
        )}

        {isDeleted ? (
          <div className={styles.deletedPlaceholder}>
            {t('comms.messages.deleted')}
          </div>
        ) : (
          <>
            {editDraft !== null ? (
              <div className={styles.editRow}>
                <textarea
                  className={styles.editTextarea}
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !isImeComposing(e)) { e.preventDefault(); commitEdit(); }
                    else if (e.key === 'Escape') { e.preventDefault(); cancelEdit(); }
                  }}
                  autoFocus
                />
                <div className={styles.editActions}>
                  {editError && (
                    <span className={styles.editErrorMsg}>{t('comms.messages.editError')}</span>
                  )}
                  <button type="button" className={styles.editCancel} onClick={cancelEdit}>
                    {t('comms.messages.editCancel')}
                  </button>
                  <button type="button" className={styles.editSave} onClick={commitEdit}>
                    {t('comms.messages.editSave')}
                  </button>
                </div>
              </div>
            ) : (
              hasText && (
                <div className={styles.textRow}>
                  <div className={styles.text}>
                    {message.segments.map((segment, index) => (
                      <SegmentView key={index} segment={segment} siteId={siteId} />
                    ))}
                    {isEdited && (
                      <span className={styles.editedTag} title={new Date(message.editedAt!).toLocaleString()}>
                        {' '}({t('comms.messages.edited')})
                      </span>
                    )}
                  </div>
                  {/* Time shown inline after text for grouped messages */}
                  {grouped && (
                    <span className={styles.timeGrouped}>
                      {isOwn && <ReadMark message={message} />}
                      {time}
                    </span>
                  )}
                </div>
              )
            )}
            {message.attachments.length > 0 ? (
              <div className={styles.attachments}>
                {message.attachments.map((attachment) => (
                  <AttachmentView key={attachment.id} siteId={siteId} attachment={attachment} />
                ))}
              </div>
            ) : localAttachments && localAttachments.length > 0 && (
              <div className={styles.attachments}>
                {localAttachments.map((attachment) => (
                  <LocalAttachmentView key={attachment.localId} attachment={attachment} />
                ))}
              </div>
            )}
            {grouped && deliveryStatus && <div className={styles.statusRow}>{deliveryStatus}</div>}
          </>
        )}
      </div>

      {/* Hidden anchor for the context menu — positioned at the right-click point */}
      {canOpenMenu && anchorPos && (
        <BaseMenu.Root open={menuOpen} onOpenChange={setMenuOpen}>
          <BaseMenu.Trigger
            ref={anchorRef}
            className={styles.contextAnchor}
            style={{ left: anchorPos.x, top: anchorPos.y } as React.CSSProperties}
            aria-hidden="true"
          />
          <BaseMenu.Portal>
            <BaseMenu.Positioner side="bottom" align="start" sideOffset={0}>
              <BaseMenu.Popup className={styles.contextPopup}>
                {canInfo && (
                  <BaseMenu.Item
                    className={styles.contextItem}
                    onClick={() => { onInfo?.(); setMenuOpen(false); }}
                  >
                    <CheckCircleIcon size={13} />
                    {t('comms.receipts.infoBtn')}
                  </BaseMenu.Item>
                )}
                {canCopy && (
                  <BaseMenu.Item
                    className={styles.contextItem}
                    onClick={copyText}
                  >
                    <CopyIcon size={13} />
                    {t('comms.messages.copyBtn')}
                  </BaseMenu.Item>
                )}
                {canEdit && onEdit && (
                  <BaseMenu.Item
                    className={styles.contextItem}
                    onClick={startEdit}
                  >
                    <PencilIcon size={13} />
                    {t('comms.messages.editBtn')}
                  </BaseMenu.Item>
                )}
                {canDelete && onDelete && (
                  <BaseMenu.Item
                    className={styles.contextItemDanger}
                    onClick={() => { onDelete(); setMenuOpen(false); }}
                  >
                    <TrashIcon size={13} />
                    {t('comms.messages.deleteBtn')}
                  </BaseMenu.Item>
                )}
                {canSendDirectMessage && (canCopy || canEdit || canDelete) && (
                  <BaseMenu.Separator className={styles.contextSeparator} />
                )}
                {canSendDirectMessage && (
                  <BaseMenu.Item
                    className={styles.contextItem}
                    onClick={sendDirectMessage}
                  >
                    <MessageSquareIcon size={13} />
                    {t('comms.messages.sendDmBtn')}
                  </BaseMenu.Item>
                )}
              </BaseMenu.Popup>
            </BaseMenu.Positioner>
          </BaseMenu.Portal>
        </BaseMenu.Root>
      )}
    </div>
  );
}

// Callbacks (onRetry, onDelete, onEdit) are recreated by the parent on every
// render because they close over the current message. We skip comparing them —
// their identity never affects what the row renders, only what happens when the
// user acts. What actually drives a re-render is message content changing.
export const MessageItem = memo(MessageItemInner, (prev, next) =>
  prev.message.id === next.message.id &&
  prev.message.authorName === next.message.authorName &&
  prev.message.status === next.message.status &&
  prev.message.deletedAt === next.message.deletedAt &&
  prev.message.editedAt === next.message.editedAt &&
  prev.message.segments === next.message.segments &&
  prev.message.attachments === next.message.attachments &&
  prev.message.readCount === next.message.readCount &&
  prev.message.audience === next.message.audience &&
  prev.localAttachments === next.localAttachments &&
  prev.isOwn === next.isOwn &&
  prev.grouped === next.grouped &&
  !!prev.onInfo === !!next.onInfo,
);
